/**
 * useIVOverlay — IV Result Polygons on Map
 *
 * For each assigned measurement (across all visible sessions), renders a filled
 * polygon covering the full module grid of the corresponding string label.
 * Color encodes result_pct deviation:
 *   green  → result_pct >= 0%
 *   orange → result_pct in [-5%, 0%)
 *   red    → result_pct < -5%
 *   grey   → assigned but no result_pct
 *
 * Falls back to a circle marker when no module grid config is present.
 *
 * Per-session visibility controlled by mapStore.ivSessionVisibility.
 * Display metric (label value) controlled by mapStore.ivDisplayMetric.
 * Uses the imperative subscribe() pattern to avoid re-render cascades.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useIVStore } from '../../stores/ivStore';
import { useMapStore } from '../../stores/mapStore';
import { transformPoint } from '../../domain/layout/transform';
import { stringGridFrame } from '../../domain/topology/moduleGrid';
import { metersToDeg } from '../../utils/geometry';
import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import type { IVMeasurement } from '../../types/iv';
import type { SiteLayout } from '../../domain/layout/types';

// ── Layer / source IDs ──────────────────────────────────────────────────────

const SOURCE_POLY_ID  = 'iv-result-poly';
const SOURCE_POINT_ID = 'iv-result-point';
const FILL_LAYER_ID   = 'iv-result-fill';
const LINE_LAYER_ID   = 'iv-result-line';
const TEXT_LAYER_ID   = 'iv-result-text';

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * F-01D-2: resolve the effective list of layouts the IV overlay should
 * consider. Mirrors the resolution in useLayoutOverlay so both hooks see
 * the same set of layouts in the same order. Prefers the canonical
 * `siteLayouts[]`; falls back to a length-1 array wrapping the legacy
 * singleton when the project hasn't been normalized yet (extremely rare
 * after F-01A's load-time normalization).
 *
 * Exported for unit testing.
 */
export function getLayoutsForIv(
  data: ReturnType<typeof useProjectStore.getState>['data'],
): SiteLayout[] {
  const arr = data.siteLayouts && data.siteLayouts.length > 0
    ? data.siteLayouts
    : (data.siteLayout ? [data.siteLayout] : []);
  return arr.filter((l): l is SiteLayout => !!l);
}

/**
 * F-01D-2: decide which layout a single IV measurement should render
 * against. Two-tier strategy:
 *
 *   Tier 1 — Explicit `layout_id`: if the measurement carries a non-null
 *   `layout_id` and a layout with that id exists in `layouts`, return it.
 *
 *   Tier 2 — Legacy fallback (first-match-wins): when `layout_id` is null
 *   (pre-F-01D-1 rows, never assigned through the new path) OR the
 *   explicit id is stale (the owning layout has been removed since), walk
 *   `layouts` in array order and return the first layout whose
 *   `textEntities` contain an entity matching `m.map_label`. This
 *   preserves the previous singleton behaviour for legacy assignments and
 *   degrades gracefully when a layout has been removed.
 *
 * Returns `undefined` when no layout can render this measurement (no
 * `map_label`, or no layout has a matching entity). Callers MUST tolerate
 * `undefined` — F-01D-2 contract says one bad measurement never breaks
 * the rest of the overlay.
 *
 * Exported for unit testing.
 */
export function findLayoutForMeasurement(
  m: IVMeasurement,
  layouts: SiteLayout[],
): SiteLayout | undefined {
  // Tier 1: explicit id wins.
  if (m.layout_id) {
    const exact = layouts.find((l) => l.id === m.layout_id);
    if (exact) return exact;
    // Fall through to Tier 2 — explicit id is stale (layout removed), so
    // we degrade to legacy first-match-wins so the marker still renders.
  }
  // Tier 2: legacy / fallback. Requires a map_label to look up against.
  if (!m.map_label) return undefined;
  for (const l of layouts) {
    if (!l.transform || !l.textEntities) continue;
    const found = l.textEntities.some((e) => {
      const cls = e.classificationOverride ?? e.classification;
      return cls?.value === m.map_label;
    });
    if (found) return l;
  }
  return undefined;
}

/**
 * Build a per-layout entity lookup so `buildGeoJSONs` doesn't rebuild
 * a Map for every measurement. Keyed by `layout.id`, each value is a
 * `Map<classifiedValue, textEntity>`.
 */
function buildEntityLookups(
  layouts: SiteLayout[],
): Map<string, Map<string, SiteLayout['textEntities'][0]>> {
  const out = new Map<string, Map<string, SiteLayout['textEntities'][0]>>();
  for (const l of layouts) {
    if (!l.textEntities) {
      out.set(l.id, new Map());
      continue;
    }
    const inner = new Map<string, SiteLayout['textEntities'][0]>();
    for (const entity of l.textEntities) {
      const cls = entity.classificationOverride ?? entity.classification;
      if (cls?.value) inner.set(cls.value, entity);
    }
    out.set(l.id, inner);
  }
  return out;
}

/** Format the display value for the map label based on selected metric. */
function formatDisplayValue(m: IVMeasurement, metric: string): string {
  if (metric === 'result') {
    const pct = m.result_pct ?? null;
    return pct === null ? '' : (pct >= 0 ? '+' : '') + Math.round(pct) + '%';
  }
  const fieldMap: Record<string, () => number | null> = {
    voltage_oc:  () => m.voltage_oc,
    current_sc:  () => m.current_sc,
    voltage_mpp: () => m.voltage_mpp,
    current_mpp: () => m.current_mpp,
    power_mpp:   () => m.power_mpp,
    fill_factor: () => m.fill_factor,
    irradiance:  () => m.irradiance,
    module_temp: () => m.module_temp,
    pmax_opc:    () => m.extras?.pmax_opc != null ? Number(m.extras.pmax_opc) : null,
  };
  const units: Record<string, string> = {
    voltage_oc: 'V', current_sc: 'A', voltage_mpp: 'V', current_mpp: 'A',
    power_mpp: 'W', fill_factor: '%', irradiance: 'W/m²', module_temp: '°C', pmax_opc: 'W',
  };
  const val = fieldMap[metric]?.() ?? null;
  if (val === null) return '—';
  return `${Math.round(val * 10) / 10}${units[metric] ?? ''}`;
}

/** Shared feature properties for both polygon and point features. */
function buildProps(m: IVMeasurement, index: number, displayMetric: string) {
  const testNum =
    m.extras?.test_number != null
      ? String(m.extras.test_number)
      : m.source_row_index != null
        ? String(m.source_row_index)
        : String(index + 1);

  const pct = m.result_pct ?? null;

  return {
    map_label: m.map_label,
    result_pct: pct,
    result: m.result ?? null,
    test_num: testNum,
    display_value: formatDisplayValue(m, displayMetric),
    color_key:
      pct === null ? 'unknown'
      : pct >= 0   ? 'pass'
      : pct >= -5  ? 'warn'
      : 'fail',
  };
}

/**
 * Compute the 5-point closed ring (bounding rectangle) of the full module
 * grid for a string, using the same math as generateModuleGridGeoJSON.
 */
function buildStringRing(
  gridLng: number,
  gridLat: number,
  directionDeg: number,
  modulesPerRow: number,
  rows: number,
  w: number,
  g: number,
  h: number,
  rowGap: number,
): [number, number][] {
  const halfAlong = (modulesPerRow * (w + g) - g) / 2;
  const halfPerp  = (rows - 1) * (h + rowGap) / 2 + h / 2;

  const rad = (directionDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const { dLng, dLat } = metersToDeg(1, 1, gridLat);

  const corners: [number, number][] = [
    [-halfAlong, -halfPerp],
    [ halfAlong, -halfPerp],
    [ halfAlong,  halfPerp],
    [-halfAlong,  halfPerp],
  ];

  const ring = corners.map<[number, number]>(([ax, perp]) => [
    gridLng + (ax * cos - perp * sin) * dLng,
    gridLat + (ax * sin + perp * cos) * dLat,
  ]);
  ring.push(ring[0]);
  return ring;
}

/**
 * Build two GeoJSON collections from the measurements of all visible sessions:
 *  - polys:  one Polygon per string (for fill + outline layers)
 *  - points: one Point per string (at grid center, for text label)
 *
 * F-01D-2: each measurement is now resolved against the correct owning
 * layout via `findLayoutForMeasurement` — explicit `layout_id` first, then
 * legacy first-match-wins fallback across `layouts[]` in array order. This
 * means the same `map_label` value present in two different layouts can
 * render two separate IV markers at two different positions, one per owning
 * layout.
 *
 * Deduplication key: `${session_id}:${layout.id}:${map_label}` so a single
 * session can render one marker per (layout, label) pair. A measurement
 * pinned to layout A and another pinned to layout B with the same map_label
 * therefore both render — no incorrect collapse.
 *
 * Only includes strings whose color_key is enabled in `filters`.
 */
function buildGeoJSONs(
  layouts: SiteLayout[],
  measurements: IVMeasurement[],
  filters: Record<string, boolean>,
  displayMetric: string,
): { polys: GeoJSON.FeatureCollection; points: GeoJSON.FeatureCollection } {
  const polyFeatures:  GeoJSON.Feature[] = [];
  const pointFeatures: GeoJSON.Feature[] = [];
  const seen = new Set<string>();

  // Build per-layout entity lookups once so the inner loop stays O(1) per
  // measurement instead of rebuilding the Map every iteration.
  const entityLookups = buildEntityLookups(layouts);

  for (let i = 0; i < measurements.length; i++) {
    const m = measurements[i];
    if (!m.map_label) continue;

    const layout = findLayoutForMeasurement(m, layouts);
    if (!layout || !layout.transform) continue;

    // Per-(session, layout, label) dedup. With multi-layout the same label
    // value in two different layouts must NOT collapse into a single
    // feature — they belong to different physical strings.
    const dedupKey = `${m.session_id}:${layout.id}:${m.map_label}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const entityByValue = entityLookups.get(layout.id);
    const entity = entityByValue?.get(m.map_label);
    const coords = entity
      ? transformPoint(layout.transform, entity.cx, entity.cy)
      : null;
    if (!coords) continue;

    const [lng, lat] = coords;
    const props = buildProps(m, i, displayMetric);

    // Skip if this category is filtered out
    if (!(filters[props.color_key] ?? true)) continue;

    const config = layout.moduleGridConfig;
    const hasGrid = !!config;

    if (!hasGrid || !config || !entity) {
      // Fallback: point only
      pointFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: props,
      });
      continue;
    }

    // Single geometry authority (2026-09-02): the frame resolves site
    // defaults + per-string overrides + grid-origin offsets exactly as the
    // module-cell generator does — this ring can no longer drift from it.
    const frame = stringGridFrame(layout, entity);
    if (!frame) {
      pointFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: props,
      });
      continue;
    }
    const { gridLng, gridLat } = frame;
    const ring = buildStringRing(
      gridLng, gridLat, frame.directionDeg, frame.modulesPerRow, frame.rows,
      frame.w, frame.g, frame.h, frame.rowGap,
    );

    polyFeatures.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [ring] },
      properties: props,
    });

    pointFeatures.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [gridLng, gridLat] },
      properties: props,
    });
  }

  return {
    polys:  { type: 'FeatureCollection', features: polyFeatures },
    points: { type: 'FeatureCollection', features: pointFeatures },
  };
}

// ── Mapbox layer management ─────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLOR_EXPR: any = [
  'match', ['get', 'color_key'],
  'pass',    '#22c55e',
  'warn',    '#f97316',
  'fail',    '#ef4444',
  /* unknown */ '#9ca3af',
];

function addLayers(map: MapboxMap, visible: boolean) {
  const v = visible ? 'visible' : 'none';

  if (!map.getSource(SOURCE_POLY_ID)) {
    map.addSource(SOURCE_POLY_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SOURCE_POINT_ID)) {
    map.addSource(SOURCE_POINT_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(FILL_LAYER_ID)) {
    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_POLY_ID,
      layout: { visibility: v },
      paint: {
        'fill-color': COLOR_EXPR,
        'fill-opacity': 0.35,
      },
    });
  }

  if (!map.getLayer(LINE_LAYER_ID)) {
    map.addLayer({
      id: LINE_LAYER_ID,
      type: 'line',
      source: SOURCE_POLY_ID,
      layout: { visibility: v },
      paint: {
        'line-color': COLOR_EXPR,
        'line-width': 1.5,
        'line-opacity': 0.9,
      },
    });
  }

  if (!map.getLayer(TEXT_LAYER_ID)) {
    map.addLayer({
      id: TEXT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_POINT_ID,
      layout: {
        visibility: v,
        'text-field': [
          'concat',
          ['get', 'test_num'],
          '\n',
          ['get', 'display_value'],
        ],
        'text-size': 10,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-justify': 'center',
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.75)',
        'text-halo-width': 1.5,
      },
    });
  }
}

function applyVisibility(map: MapboxMap, visible: boolean) {
  const v = visible ? 'visible' : 'none';
  if (map.getLayer(FILL_LAYER_ID)) map.setLayoutProperty(FILL_LAYER_ID, 'visibility', v);
  if (map.getLayer(LINE_LAYER_ID)) map.setLayoutProperty(LINE_LAYER_ID, 'visibility', v);
  if (map.getLayer(TEXT_LAYER_ID)) map.setLayoutProperty(TEXT_LAYER_ID, 'visibility', v);
}

function removeLayers(map: MapboxMap) {
  if (map.getLayer(TEXT_LAYER_ID))   map.removeLayer(TEXT_LAYER_ID);
  if (map.getLayer(LINE_LAYER_ID))   map.removeLayer(LINE_LAYER_ID);
  if (map.getLayer(FILL_LAYER_ID))   map.removeLayer(FILL_LAYER_ID);
  if (map.getSource(SOURCE_POINT_ID)) map.removeSource(SOURCE_POINT_ID);
  if (map.getSource(SOURCE_POLY_ID))  map.removeSource(SOURCE_POLY_ID);
}

function updateSources(
  map: MapboxMap,
  layouts: SiteLayout[],
  sessionVisibility: Record<string, boolean>,
  sessionCache: Record<string, IVMeasurement[]>,
  filters: Record<string, boolean>,
  displayMetric: string,
) {
  // Merge measurements from all visible sessions
  const measurements: IVMeasurement[] = [];
  for (const [sessionId, visible] of Object.entries(sessionVisibility)) {
    if (!visible) continue;
    const cached = sessionCache[sessionId];
    if (cached) measurements.push(...cached);
  }
  const { polys, points } = buildGeoJSONs(layouts, measurements, filters, displayMetric);
  (map.getSource(SOURCE_POLY_ID)  as GeoJSONSource | undefined)?.setData(polys);
  (map.getSource(SOURCE_POINT_ID) as GeoJSONSource | undefined)?.setData(points);
}

// ── Hook ────────────────────────────────────────────────────────────────────

export default function useIVOverlay(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
) {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!isMapReady) return;

    const map = mapRef.current;
    if (!map) return;

    const initialMapState = useMapStore.getState();
    // Layer visibility follows the master switch only. The per-session
    // selection decides WHAT is in the sources (updateSources below), so
    // switching the layer off/on is a pure show/hide that keeps the subset.
    addLayers(map, initialMapState.ivLayerVisible);
    mountedRef.current = true;

    // Initial paint
    updateSources(
      map,
      getLayoutsForIv(useProjectStore.getState().data),
      initialMapState.ivSessionVisibility,
      useIVStore.getState().ivSessionCache,
      initialMapState.ivResultFilters,
      initialMapState.ivDisplayMetric,
    );

    // Subscribe to mapStore for visibility + filter + metric changes
    const unsubMap = useMapStore.subscribe((state, prev) => {
      const m = mapRef.current;
      if (!m || !mountedRef.current) return;
      if (state.ivLayerVisible !== prev.ivLayerVisible) {
        applyVisibility(m, state.ivLayerVisible);
      }
      if (
        state.ivSessionVisibility !== prev.ivSessionVisibility ||
        state.ivResultFilters !== prev.ivResultFilters ||
        state.ivDisplayMetric !== prev.ivDisplayMetric
      ) {
        updateSources(
          m,
          getLayoutsForIv(useProjectStore.getState().data),
          state.ivSessionVisibility,
          useIVStore.getState().ivSessionCache,
          state.ivResultFilters,
          state.ivDisplayMetric,
        );
      }
    });

    // Subscribe to projectStore (layout changes — siteLayouts OR alias)
    const unsubProject = useProjectStore.subscribe((state, prev) => {
      const m = mapRef.current;
      if (!m || !mountedRef.current) return;
      // F-01D-2: react to changes in the canonical array OR the deprecated
      // singleton alias so legacy projects (still on alias only) keep working.
      if (
        state.data.siteLayouts !== prev.data.siteLayouts ||
        state.data.siteLayout !== prev.data.siteLayout
      ) {
        const ms = useMapStore.getState();
        updateSources(m, getLayoutsForIv(state.data), ms.ivSessionVisibility, useIVStore.getState().ivSessionCache, ms.ivResultFilters, ms.ivDisplayMetric);
      }
    });

    // Subscribe to ivStore (session cache changes)
    const unsubIV = useIVStore.subscribe((state, prev) => {
      const m = mapRef.current;
      if (!m || !mountedRef.current) return;
      if (state.ivSessionCache !== prev.ivSessionCache) {
        const ms = useMapStore.getState();
        updateSources(m, getLayoutsForIv(useProjectStore.getState().data), ms.ivSessionVisibility, state.ivSessionCache, ms.ivResultFilters, ms.ivDisplayMetric);
      }
    });

    return () => {
      unsubMap();
      unsubProject();
      unsubIV();
      mountedRef.current = false;
      const m = mapRef.current;
      if (m) removeLayers(m);
    };
  }, [isMapReady, mapRef]);
}
